import { useCallback, useEffect, useRef, useState } from 'react';
import { defaultCameraPosition } from '../cameraPositions';

// ヒントで視点を動かす時間（ms）。もとの視点へ戻すときも同じ
export const HINT_DURATION = 1200;

// ヒント（おかしさが分かる視点で見る）のカメラ移動。X-1（PartSelect）と X-2（ShapeChange）で共用する。
// 渡した ModelViewer の ref を、シーンを作り直さずに命令的にイージング移動させる
// （prop で視点を渡すとキャンバスごと再構築され、動きが見えずに飛ぶため）。
// トグル式（2026-10-12）：showHint でヒントの視点へ動かし（shown=true）、resetView で
// **もとの視点（既定視点＝デッサンを描いた視点）**へ戻す（shown=false）。もとの見え方と見比べられるようにするため。
// 2026-10-19変更：旧＝ヒントを押す直前の視点へ戻していたが、学習者が回したあとの視点に戻っても
// 「結局どこ？」となり、デッサンと見比べられないため。押す直前の視点は X-1→X-2 の引き継ぎ（restingPosition）にだけ使う。
// 移動中（行き・帰りとも）は animating=true になり、ドラッグ操作も止める。呼び出し側は animating の間、
// ヒントボタンと画面を切り替えるボタン（次のステップ等）を押せなくする
// （移動中に押すと動きが重なる・キャンバスが作り直されて途中の視点が次の画面に引き継がれるため）。
// orbitEnabled：移動が終わったあとに戻すドラッグ操作の可否（画面の設定どおりに戻す）
// resetKey：問題が変わったら（値が変わったら）トグルを「ヒント前」に戻す
// restingPosition(i)：ヒントの視点を表示中なら、i 番目のキャンバスの「ヒントを押す直前の視点」を返す（表示中でなければ null）。
//   画面を進めるときに視点を引き継ぐ側（X-1→X-2）が使う。ヒントの視点のまま引き継ぐと、次の画面では
//   ヒントを押しても動かず、【もとの視点に戻す】もヒントの視点に戻るだけになるため（2026-10-14）。
export function useHintCamera(viewerRefs, orbitEnabled = true, resetKey = null) {
  const [animating, setAnimating] = useState(false);
  const [shown, setShown] = useState(false);
  const animatingRef = useRef(false);
  const shownRef = useRef(false);
  const timerRef = useRef(null);
  const originRef = useRef([]); // viewerRefs と同じ並びで、ヒントを押す直前の視点

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  useEffect(() => {
    setShown(false);
    shownRef.current = false;
    originRef.current = [];
  }, [resetKey]);

  // targets(i) が返す視点へ、準備のできたキャンバスを一斉に動かす
  const move = (targets, nextShown) => {
    if (animatingRef.current) return false;
    const viewers = viewerRefs
      .map((r, i) => ({ v: r && r.current, i }))
      .filter(({ v }) => v && v.getCameraPosition && v.getCameraPosition()); // 描画の準備ができたものだけ
    if (viewers.length === 0) return false;

    animatingRef.current = true;
    setAnimating(true);
    setShown(nextShown);
    shownRef.current = nextShown;
    viewers.forEach(({ v, i }) => {
      const to = targets(i, v);
      if (!to) return;
      v.setOrbitEnabled(false);
      v.animateCameraTo(to, HINT_DURATION, () => v.setOrbitEnabled(orbitEnabled));
    });
    // 完了はコールバックでなく時間で解除する（途中でキャンバスが消えてもボタンが押せないままにならないように）
    timerRef.current = setTimeout(() => {
      animatingRef.current = false;
      setAnimating(false);
      timerRef.current = null;
    }, HINT_DURATION + 100);
    return true;
  };

  const showHint = useCallback((position) => {
    if (animatingRef.current) return false;
    originRef.current = viewerRefs.map((r) => (r && r.current && r.current.getCameraPosition()) || null);
    return move(() => position, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orbitEnabled]);

  const resetView = useCallback(() => move(() => defaultCameraPosition, false),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [orbitEnabled]);

  const restingPosition = useCallback((i) => (shownRef.current ? originRef.current[i] || null : null), []);

  return { animating, shown, showHint, resetView, restingPosition };
}
